import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { gql } from '../lib/api';
import {
  BENCHMARK_QUERY,
  SET_TARGET_MUTATION,
  STORES_QUERY,
  TARGETS_QUERY,
} from '../graphql/queries';
import { useSelectedStore } from '../hooks/useSelectedStore';

interface Store {
  id: string;
  name: string;
}
interface Target {
  storeId: string;
  yearMonth: string;
  foodCostPct: number;
  laborCostPct: number;
  flPct: number;
  rentPct: number | null;
  otherPct: number | null;
  profitPct: number | null;
  updatedAt?: string;
}

interface Benchmark {
  foodCostPct: number;
  laborCostPct: number;
  flPct: number;
  rentPct: number;
  otherPct: number;
  profitPct: number;
}

interface TargetInputForm {
  yearMonth: string;
  foodCostPct: number;
  laborCostPct: number;
  flPct: number;
  rentPct: string; // 任意フィールドは空文字許容
  otherPct: string;
  profitPct: string;
}

export const TargetsPage = () => {
  const qc = useQueryClient();
  const storesQ = useQuery({
    queryKey: ['stores'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_QUERY),
  });
  const stores = storesQ.data?.stores ?? [];

  const benchmarkQ = useQuery({
    queryKey: ['benchmark'],
    queryFn: () => gql<{ benchmark: Benchmark }>(BENCHMARK_QUERY),
  });
  const benchmark = benchmarkQ.data?.benchmark;

  const [storeId, setStoreId] = useSelectedStore();
  const effectiveStoreId = storeId || stores[0]?.id || '';
  const today = new Date();
  const fromYearMonth = `${today.getFullYear() - 1}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const toYearMonth = `${today.getFullYear() + 1}-${String(today.getMonth() + 1).padStart(2, '0')}`;

  const targetsQ = useQuery({
    enabled: !!effectiveStoreId,
    queryKey: ['targets', effectiveStoreId, fromYearMonth, toYearMonth],
    queryFn: () =>
      gql<{ targets: Target[] }>(TARGETS_QUERY, {
        storeId: effectiveStoreId,
        fromYearMonth,
        toYearMonth,
      }),
  });

  const setMutation = useMutation({
    mutationFn: (vars: { storeId: string; yearMonth: string; input: Record<string, unknown> }) =>
      gql(SET_TARGET_MUTATION, vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['targets'] });
    },
  });

  const [draft, setDraft] = useState<TargetInputForm>({
    yearMonth: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
    foodCostPct: 30,
    laborCostPct: 30,
    flPct: 55,
    rentPct: '10',
    otherPct: '20',
    profitPct: '10',
  });

  const submit = () => {
    const input: Record<string, unknown> = {
      foodCostPct: draft.foodCostPct,
      laborCostPct: draft.laborCostPct,
      flPct: draft.flPct,
    };
    if (draft.rentPct.trim() !== '') input.rentPct = Number(draft.rentPct);
    if (draft.otherPct.trim() !== '') input.otherPct = Number(draft.otherPct);
    if (draft.profitPct.trim() !== '') input.profitPct = Number(draft.profitPct);
    setMutation.mutate({
      storeId: effectiveStoreId,
      yearMonth: draft.yearMonth,
      input,
    });
  };

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: '1.4rem', marginBottom: '1rem' }}>目標値設定</h1>

      <section style={notice}>
        <p style={{ margin: 0 }}>
          店舗 × 月単位で構成比目標を上書き設定します。未設定の場合、トップページでは
          全社デフォルト
          {benchmark
            ? `（F:${benchmark.foodCostPct}% / L:${benchmark.laborCostPct}% / FL:${benchmark.flPct}% / 家賃:${benchmark.rentPct}% / 他:${benchmark.otherPct}% / 利益:${benchmark.profitPct}%）`
            : '（30/30/55/10/20/10）'}
          が適用されます。
        </p>
      </section>

      <section style={card}>
        <h2 style={sectionTitle}>新規／更新</h2>
        <div style={{ display: 'grid', gap: '0.6rem', gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <label>
            店舗
            <select
              value={effectiveStoreId}
              onChange={(e) => setStoreId(e.target.value)}
              style={input}
            >
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            対象月（YYYY-MM）
            <input
              type="month"
              value={draft.yearMonth}
              onChange={(e) => setDraft({ ...draft, yearMonth: e.target.value })}
              style={input}
            />
          </label>
          <label>
            食材費率 F（%）
            <input
              type="number"
              step="0.1"
              value={draft.foodCostPct}
              onChange={(e) => setDraft({ ...draft, foodCostPct: Number(e.target.value) })}
              style={input}
            />
          </label>
          <label>
            人件費率 L（%）
            <input
              type="number"
              step="0.1"
              value={draft.laborCostPct}
              onChange={(e) => setDraft({ ...draft, laborCostPct: Number(e.target.value) })}
              style={input}
            />
          </label>
          <label>
            FL 率（%）
            <input
              type="number"
              step="0.1"
              value={draft.flPct}
              onChange={(e) => setDraft({ ...draft, flPct: Number(e.target.value) })}
              style={input}
            />
          </label>
          <label>
            家賃率（%、任意）
            <input
              type="number"
              step="0.1"
              value={draft.rentPct}
              onChange={(e) => setDraft({ ...draft, rentPct: e.target.value })}
              placeholder="未入力でデフォルト"
              style={input}
            />
          </label>
          <label>
            他率（%、任意）
            <input
              type="number"
              step="0.1"
              value={draft.otherPct}
              onChange={(e) => setDraft({ ...draft, otherPct: e.target.value })}
              placeholder="未入力でデフォルト"
              style={input}
            />
          </label>
          <label>
            利益率（%、任意）
            <input
              type="number"
              step="0.1"
              value={draft.profitPct}
              onChange={(e) => setDraft({ ...draft, profitPct: e.target.value })}
              placeholder="未入力でデフォルト"
              style={input}
            />
          </label>
        </div>
        <button
          type="button"
          disabled={!effectiveStoreId || setMutation.isPending}
          onClick={submit}
          style={{ ...primaryBtn, marginTop: '0.75rem' }}
        >
          {setMutation.isPending ? '保存中…' : '保存'}
        </button>
        {setMutation.isSuccess && (
          <p style={{ color: '#006d8d', marginTop: '0.5rem' }}>保存しました</p>
        )}
        {setMutation.isError && (
          <p style={{ color: '#aa0b56', marginTop: '0.5rem' }}>
            エラー：{(setMutation.error as Error).message}
          </p>
        )}
      </section>

      <section style={card}>
        <h2 style={sectionTitle}>登録済み（{effectiveStoreId || '—'}）</h2>
        {targetsQ.isLoading ? (
          <p style={muted}>読込中…</p>
        ) : (targetsQ.data?.targets.length ?? 0) === 0 ? (
          <p style={muted}>まだ登録されていません。</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>対象月</th>
                <th style={{ ...th, textAlign: 'right' }}>F</th>
                <th style={{ ...th, textAlign: 'right' }}>L</th>
                <th style={{ ...th, textAlign: 'right' }}>FL</th>
                <th style={{ ...th, textAlign: 'right' }}>家賃</th>
                <th style={{ ...th, textAlign: 'right' }}>他</th>
                <th style={{ ...th, textAlign: 'right' }}>利益</th>
                <th style={th}>更新日時</th>
              </tr>
            </thead>
            <tbody>
              {targetsQ.data?.targets.map((t) => (
                <tr key={t.yearMonth}>
                  <td style={td}>{t.yearMonth}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{t.foodCostPct.toFixed(1)}%</td>
                  <td style={{ ...td, textAlign: 'right' }}>{t.laborCostPct.toFixed(1)}%</td>
                  <td style={{ ...td, textAlign: 'right' }}>{t.flPct.toFixed(1)}%</td>
                  <td style={{ ...td, textAlign: 'right', color: t.rentPct == null ? '#adb5bd' : undefined }}>
                    {t.rentPct != null ? `${t.rentPct.toFixed(1)}%` : '既定'}
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: t.otherPct == null ? '#adb5bd' : undefined }}>
                    {t.otherPct != null ? `${t.otherPct.toFixed(1)}%` : '既定'}
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: t.profitPct == null ? '#adb5bd' : undefined }}>
                    {t.profitPct != null ? `${t.profitPct.toFixed(1)}%` : '既定'}
                  </td>
                  <td style={td}>{t.updatedAt ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '1rem 1.25rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
  marginBottom: '1rem',
};
const notice: React.CSSProperties = {
  background: '#e7f5ff',
  border: '1px solid #a5d8ff',
  color: '#1864ab',
  borderRadius: 8,
  padding: '0.6rem 0.9rem',
  marginBottom: '1rem',
  fontSize: '0.85rem',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '0.95rem',
  marginTop: 0,
  marginBottom: '0.75rem',
  color: '#495057',
};
const muted: React.CSSProperties = { color: '#868e96', fontSize: '0.9rem' };
const input: React.CSSProperties = {
  display: 'block',
  marginTop: 4,
  padding: '0.4rem 0.6rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  width: '100%',
};
const primaryBtn: React.CSSProperties = {
  padding: '0.5rem 1rem',
  borderRadius: 6,
  border: '1px solid #aa0b56',
  background: '#aa0b56',
  color: '#fff',
  cursor: 'pointer',
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #dee2e6',
  background: '#f8f9fa',
};
const td: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #f1f3f5',
};
